import React from 'react';
import ActionsMenu from '../../../components/ActionsMenu';
import { useToolAccess } from '../../../tools/ToolAccessContext';

/**
 * DeploymentList component - Displays a table of deployments for the current project
 * @param {Object} props - Component props
 * @param {Array} props.deployments - List of deployments to display
 * @param {string} props.activeProjectName - Name of the active project
 * @param {Function} props.onOpenDesigner - Handler for opening designer
 * @param {Function} props.onOpenIntegration - Handler for opening integration settings
 * @param {Function} props.onOpenAnalytics - Handler for opening analytics
 * @param {Function} props.onDeleteDeployment - Handler for delete button click
 * @param {Function} props.onCreateNew - Handler for new deployment button click
 * @param {string} props.error - Error message to display
 * @param {string} props.successMsg - Success message to display
 */
const DeploymentList = ({
    deployments,
    activeProjectName,
    onOpenDesigner,
    onOpenIntegration,
    onOpenAnalytics,
    onDeleteDeployment,
    onCreateNew,
    error,
    successMsg
}) => {
    const { canEditCurrent } = useToolAccess();
    return (
        <>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                <div>
                    <h1 style={{ fontSize: '1.35rem', margin: 0, color: 'var(--text-primary)' }}>
                        Deployments: <span style={{ color: 'var(--accent)' }}>{activeProjectName}</span>
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: '0.25rem 0 0 0' }}>
                        Manage localized configurations and embed targets for each family and design layout.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onCreateNew}
                    className="btn-primary"
                    disabled={!canEditCurrent}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: 'var(--text-primary)', color: 'var(--bg-primary)', padding: '0.6rem 1.25rem' }}
                >
                    <i className="fa-solid fa-plus"></i> New Deployment
                </button>
            </header>

            {error && (
                <div style={{ padding: '0.75rem 1rem', background: 'rgba(220,38,38,0.08)', border: '1px solid var(--error)', borderRadius: 'var(--radius)', color: 'var(--error)', fontSize: '0.85rem' }}>
                    {error}
                </div>
            )}
            {successMsg && (
                <div style={{ padding: '0.75rem 1rem', background: 'rgba(5,150,105,0.08)', border: '1px solid var(--success)', borderRadius: 'var(--radius)', color: 'var(--success)', fontSize: '0.85rem' }}>
                    {successMsg}
                </div>
            )}

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg-card)' }}>
                {deployments.length === 0 ? (
                    <EmptyState />
                ) : (
                    <DeploymentTable
                        deployments={deployments}
                        onOpenDesigner={onOpenDesigner}
                        onOpenIntegration={onOpenIntegration}
                        onOpenAnalytics={onOpenAnalytics}
                        onDeleteDeployment={onDeleteDeployment}
                    />
                )}
            </div>
        </>
    );
};

/**
 * Empty state component shown when no deployments exist
 */
const EmptyState = () => (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'var(--text-secondary)', padding: '4rem 0', gap: '1rem', textAlign: 'center' }}>
        <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
        <div>
            <h4 style={{ margin: 0, color: 'var(--text-primary)' }}>No active deployments</h4>
            <p style={{ fontSize: '0.85rem', margin: '0.25rem 0 0 0' }}>
                Create a deployment to map a family to a GUI layout and generate an embed token.
            </p>
        </div>
    </div>
);

/**
 * Table component for displaying deployments
 */
const DeploymentTable = ({ deployments, onOpenDesigner, onOpenIntegration, onOpenAnalytics, onDeleteDeployment }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
        <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0 }}>
            <tr>
                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Name</th>
                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Family</th>
                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>GUI Layout Tag</th>
                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</th>
                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Embed Token</th>
                <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textAlign: 'right' }}>Actions</th>
            </tr>
        </thead>
        <tbody>
            {deployments.map(dep => (
                <DeploymentRow
                    key={dep.id}
                    deployment={dep}
                    onOpenDesigner={onOpenDesigner}
                    onOpenIntegration={onOpenIntegration}
                    onOpenAnalytics={onOpenAnalytics}
                    onDeleteDeployment={onDeleteDeployment}
                />
            ))}
        </tbody>
    </table>
);

/**
 * Individual row for a deployment in the table
 */
const DeploymentRow = ({ deployment, onOpenDesigner, onOpenIntegration, onOpenAnalytics, onDeleteDeployment }) => (
    <tr style={{ borderBottom: '1px solid var(--border)', transition: 'var(--transition)' }} className="table-row-hover">
        <td style={{ padding: '0.5rem 0.75rem', fontWeight: 600, color: 'var(--text-primary)' }}>{deployment.name}</td>
        <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-secondary)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <i className="fa-solid fa-box" style={{ color: 'var(--accent)', fontSize: '0.8rem' }}></i>
                {deployment.productFamilyName || `ID: ${deployment.productFamilyId}`}
            </span>
        </td>
        <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>{deployment.guiVersionTag}</td>
        <td style={{ padding: '0.5rem 0.75rem' }}>
            <StatusBadge isActive={deployment.isActive} />
        </td>
        <td style={{ padding: '0.5rem 0.75rem' }}>
            <TokenPreview token={deployment.embedToken} />
        </td>
        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
            <ActionButtons
                deployment={deployment}
                onOpenDesigner={onOpenDesigner}
                onOpenIntegration={onOpenIntegration}
                onOpenAnalytics={onOpenAnalytics}
                onDeleteDeployment={onDeleteDeployment}
            />
        </td>
    </tr>
);

/**
 * Status badge component showing active/disabled state
 */
const StatusBadge = ({ isActive }) => (
    <span style={{
        padding: '0.2rem 0.5rem',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: isActive ? 'rgba(5, 150, 105, 0.1)' : 'rgba(220, 38, 38, 0.1)',
        color: isActive ? 'var(--success)' : 'var(--error)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.2rem'
    }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: isActive ? 'var(--success)' : 'var(--error)' }}></span>
        {isActive ? 'Active' : 'Disabled'}
    </span>
);

/**
 * Token preview component showing truncated token
 */
const TokenPreview = ({ token }) => (
    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', background: 'var(--bg-secondary)', padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--border)' }}>
        {token?.slice(0, 8)}...
    </span>
);

/**
 * Action buttons for deployment row
 */
const ActionButtons = ({ deployment, onOpenDesigner, onOpenIntegration, onOpenAnalytics, onDeleteDeployment }) => {
    const { canEditCurrent } = useToolAccess();
    return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button
            type="button"
            className="btn-secondary"
            onClick={() => onOpenDesigner(deployment)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.4rem 0.7rem',
                fontSize: '0.82rem',
                whiteSpace: 'nowrap'
            }}
            title={`Open GUI designer for ${deployment.name}`}
        >
            <i className="fa-solid fa-compass-drafting"></i>
            Designer
        </button>
        <button
            type="button"
            className="btn-secondary"
            onClick={() => onOpenIntegration(deployment)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.4rem 0.7rem',
                fontSize: '0.82rem',
                whiteSpace: 'nowrap'
            }}
            title={`Open integration settings for ${deployment.name}`}
        >
            <i className="fa-solid fa-code"></i>
            Integration
        </button>
        <button
            type="button"
            className="btn-secondary"
            onClick={() => onOpenAnalytics(deployment)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.4rem 0.7rem',
                fontSize: '0.82rem',
                whiteSpace: 'nowrap'
            }}
            title={`Open analytics for ${deployment.name}`}
        >
            <i className="fa-solid fa-chart-line"></i>
            Analytics
        </button>
        {canEditCurrent && (
        <ActionsMenu
            label={`${deployment.name} more actions`}
            items={[
                {
                    label: 'Delete Deployment',
                    icon: 'fa-solid fa-trash-can',
                    danger: true,
                    onSelect: () => onDeleteDeployment(deployment.id, deployment.name)
                }
            ]}
        />
        )}
    </div>
    );
};

export default DeploymentList;
export { EmptyState, DeploymentTable, DeploymentRow, StatusBadge, TokenPreview, ActionButtons };
